import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, ClipboardList, Heart, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const icons = [MessageSquare, ClipboardList, Heart, CheckCircle];

const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].howItWorksData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="how-it-works" className="py-24 md:py-32 bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
            {t.label}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 text-lg text-gray-500 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {t.steps.map((step, i) => {
            const Icon = icons[i];
            return (
              <div
                key={i}
                className={`relative p-7 rounded-2xl border border-gray-100 bg-white transition-all duration-700 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                {/* Step icon */}
                <div className="absolute -right-2 -top-2 w-12 h-12 rounded-xl bg-[#5B4FE9] flex items-center justify-center shadow-lg shadow-indigo-200/50">
                  <Icon className="w-5 h-5 text-white" />
                </div>

                {/* Step number and title */}
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{step.title}</h3>
                  <span className="text-3xl font-bold text-gray-100 ml-auto">{step.number}</span>
                </div>

                <p className="text-sm text-gray-500 leading-relaxed">
                  {step.description}
                </p>

                {/* Detail */}
                <div className="mt-4 px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-400 italic">{step.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
